import React, { useState, useEffect, useContext } from "react";
import CustomDropdown from "../../../../components/CustomDropdown";
import { EditUserSchema } from "../../../../components/Helpers";
import Hoc from "../../../../components/HOC";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import Loader from "../../../../components/Loader";
import TextInput from "../../../../components/TextInput";
import { MdArrowBack } from "react-icons/md";
import { FaMapMarkerAlt } from "react-icons/fa";
import { FiUsers, FiUser } from "react-icons/fi";
import { UserContext } from "../../../../Context/User";

const EditEmployeeForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getUserById, editUser, userById, userLoading, editUserLoading } = useContext(UserContext);
  

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialValues, setInitialValues] = useState(null);

  const countryOptions = [
    { value: "USA", label: "USA" },
    { value: "IND", label: "IND" },
  ];

  useEffect(() => {
    const fetchUser = async () => {
      const user = await getUserById(id);
      setInitialValues({
        email: user.email || "",
        password: "", // keep for display only, not sent to API
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        gender: user.gender || "",
        phone: user.phone || "",
        addressLine1: user.addressLine1 || "",
        addressLine2: user.addressLine2 || "",
        city: user.city || "",
        state: user.state || "",
        country: user.country || "USA",
        zip: user.zip || "",
        acctType: user.acctType || "MKTG_EXEC",
        stsCd: user.stsCd || "A",
      });
    };
    fetchUser();
    // eslint-disable-next-line
  }, [id]);

  const handleEditEmployee = async (values) => {
    setIsSubmitting(true);
    try {
      // Exclude password from API payload
      const { password, ...apiValues } = values;
      await editUser(id, apiValues);
      toast.success("User Details Updated Successfully");
      navigate("/users");
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!initialValues) {
    return <Loader isLoading={true} />;
  }

  return (
    <div className="w-full h-full bg-gray-50 p-4">
      <Loader isLoading={userLoading || editUserLoading} />
      
      <div className="flex items-center mb-4">
        <button
          type="button"
          className="text-blue-600 hover:text-blue-800 mr-2"
          onClick={() => navigate("/users")}
        >
          <MdArrowBack size={20} />
        </button>
        <h2 className="text-xl font-bold text-gray-800">Edit User</h2>
      </div>
      
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={EditUserSchema}
        onSubmit={async (values) => {
          await handleEditEmployee(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          setFieldValue,
        }) => (
          <Form className="space-y-4">
            {/* Account Information Section */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2 mb-3">
                <FiUser size={18} className="text-blue-600" /> 
                Account Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Email Field (grayed out, not editable) */}
                <div>
                  <TextInput
                    value={values.email}
                    name="email"
                    placeholder="Email"
                    handleChange={() => {}}
                    legend
                    isDisabled
                    classes="bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                  {errors.email && touched.email && (
                    <div className="text-red-500 text-sm mt-1">{errors.email}</div>
                  )}
                </div>
                {/* Password Field (grayed out, not editable) */}
              </div>
            </div>

            {/* Personal Information Section */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2 mb-3">
                <FiUsers size={18} className="text-blue-600" /> 
                Personal Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* First Name (disabled) */}
                <div>
                  <TextInput
                    value={values.firstName}
                    name="firstName"
                    placeholder="First Name"
                    handleChange={handleChange}
                    legend
                    
                    className="bg-gray-100 text-gray-500"
                  />
                  {errors.firstName && touched.firstName && (
                    <div className="text-red-500 text-sm mt-1">{errors.firstName}</div>
                  )}
                </div>
                {/* Last Name (disabled) */}
                <div>
                  <TextInput
                    value={values.lastName}
                    name="lastName"
                    placeholder="Last Name"
                    handleChange={handleChange}
                    legend
                   
                    className="bg-gray-100 text-gray-500"
                  />
                  {errors.lastName && touched.lastName && (
                    <div className="text-red-500 text-sm mt-1">{errors.lastName}</div>
                  )}
                </div>
                {/* Phone (editable) */}
                <div>
                  <TextInput
                    value={values.phone}
                    name="phone"
                    placeholder="Phone Number"
                    type="tel"
                    legend
                    handleChange={e => {
                      const allowed = e.target.value.replace(/[^\d]/g, "").slice(0, 13);
                      setFieldValue("phone", allowed);
                    }}
                    maxLength={13}
                  />
                  {errors.phone && touched.phone && (
                    <div className="text-red-500 text-sm mt-1">{errors.phone}</div>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                {/* Gender (disabled) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center space-x-2">
                      <Field
                        type="radio"
                        name="gender"
                        value="M"
                        checked={values.gender === "M"}
                        className="text-gray-400"
                      />
                      <span className="text-sm text-gray-500">Male</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <Field
                        type="radio"
                        name="gender"
                        value="F"
                        checked={values.gender === "F"}
                        className="text-gray-400"
                      />
                      <span className="text-sm text-gray-500">Female</span>
                    </label>
                  </div>
                  {errors.gender && touched.gender && (
                    <div className="text-red-500 text-sm mt-1">{errors.gender}</div>
                  )}
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center space-x-2">
                      <Field
                        type="radio"
                        name="stsCd"
                        value="A"
                        checked={values.stsCd === "A"}
                        onChange={handleChange}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <Field
                        type="radio"
                        name="stsCd"
                        value="D"
                        checked={values.stsCd === "D"}
                        onChange={handleChange}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Deactive</span>
                    </label>
                  </div>
                  {errors.stsCd && touched.stsCd && (
                    <div className="text-red-500 text-sm mt-1">{errors.stsCd}</div>
                  )}
                </div>

                {/* Account Type (disabled) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Account Type</label>
                  <div className="flex items-center space-x-6">
                    
                    <label className="flex items-center space-x-2">
                      <Field
                        type="radio"
                        name="acctType"
                        value="ADMIN"
                        checked={values.acctType === "ADMIN"}
                        onChange={handleChange}
                        
                        className="text-gray-400"
                      />
                      <span className="text-sm text-gray-500">Admin</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <Field
                        type="radio"
                        name="acctType"
                        value="MKTG_EXEC"
                        checked={values.acctType === "MKTG_EXEC"}
                        onChange={handleChange}
                      
                        className="text-gray-400"
                      />
                      <span className="text-sm text-gray-500">Marketing Executive</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <Field type="radio" name="acctType" value="RECRUITER" checked={values.acctType === "RECRUITER"} className="text-blue-600 focus:ring-blue-500" />
                      <span className="text-sm text-gray-700">Recruiter</span>
                    </label>
                  </div>
                  {errors.acctType && touched.acctType && (
                    <div className="text-red-500 text-sm mt-1">{errors.acctType}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Address Information Section */}
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2 mb-3">
                <FaMapMarkerAlt size={18} className="text-blue-600" /> 
                Address Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <TextInput
                    value={values.addressLine1}
                    name="addressLine1"
                    placeholder="Address Line 1"
                    handleChange={handleChange}
                    legend
                  />
                  {errors.addressLine1 && touched.addressLine1 && (
                    <div className="text-red-500 text-sm mt-1">{errors.addressLine1}</div>
                  )}
                </div>
                <div>
                  <TextInput
                    value={values.addressLine2}
                    name="addressLine2"
                    placeholder="Address Line 2"
                    handleChange={handleChange}
                    legend
                  />
                  {errors.addressLine2 && touched.addressLine2 && (
                    <div className="text-red-500 text-sm mt-1">{errors.addressLine2}</div>
                  )}
                </div>
                <div>
                  <TextInput
                    value={values.city}
                    name="city"
                    placeholder="City"
                    handleChange={handleChange}
                    legend
                  />
                  {errors.city && touched.city && (
                    <div className="text-red-500 text-sm mt-1">{errors.city}</div>
                  )}
                </div>
                <div>
                  <TextInput
                    value={values.state}
                    name="state"
                    placeholder="State"
                    handleChange={handleChange}
                    legend
                  />
                  {errors.state && touched.state && (
                    <div className="text-red-500 text-sm mt-1">{errors.state}</div>
                  )}
                </div>
                <div>
                  <CustomDropdown
                    value={countryOptions.find(
                      (user) => user?.value === values?.country
                    )}
                    name="country"
                    handleChange={(selectedOption) =>
                      setFieldValue("country", selectedOption.value)
                    }
                    placeholder="Select Country"
                    options={countryOptions}
                  />
                  {errors.country && touched.country && (
                    <div className="text-red-500 text-sm mt-1">{errors.country}</div>
                  )}
                </div>
                <div>
                  <TextInput
                    value={values.zip}
                    name="zip"
                    placeholder="Zip Code"
                    handleChange={e => {
                      const allowed = e.target.value.replace(/[^\d]/g, "").slice(0, values.country === "USA" ? 5 : values.country === "IND" ? 6 : 6);
                      setFieldValue("zip", allowed);
                    }}
                    legend
                    maxLength={values.country === "USA" ? 5 : values.country === "IND" ? 6 : 6}
                  />
                  {((values.country === "USA" && values.zip.length !== 0 && values.zip.length !== 5) || (values.country === "IND" && values.zip.length !== 0 && values.zip.length !== 6)) && (
                    <div className="text-red-500 text-sm mt-1">
                      {values.country === "USA" && values.zip.length !== 5 && "For USA, zip code must be exactly 5 digits."}
                      {values.country === "IND" && values.zip.length !== 6 && "For India, zip code must be exactly 6 digits."}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 bg-white rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm"
                onClick={() => navigate("/users")}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`px-4 py-2 border border-transparent text-white bg-blue-600 rounded-md shadow-sm ${
                  isSubmitting
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                }`}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default Hoc(EditEmployeeForm);
